import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { bemCssModules, getTestId } from '@utils';
import { useDebounce } from '@utils/hooks';

import { accountsActions, useStore } from '@modules';
import { AccountsEstimateFeeResponse } from '@modules/accounts/types';

import FeeInformer from '@components/FeeInformer';
import MoneyInputOld from '@components/MoneyInputOld';
import LimitsOld from '@containers/Converter/LimitsOld';
import AccountSendLayoutOld from '@layouts/AccountSendLayoutOld';
import LoaderLayout from '@layouts/LoaderLayout';

import Form from './Form';
import SendVerification from './SendVerification';
import { useCustomContext, withCustomProvider } from './context';

import style from './AccountsSend.scss';

const cn = bemCssModules(style, 'AccountsSend');
const ti = getTestId('AccountsSend');

const AccountsSend: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const debounced = useDebounce(CONST.inputDebounceTime);
  const [{ global, accounts }, dispatch] = useStore();
  const { context, setContext } = useCustomContext();
  const { amount, iban } = context;

  const { iban_currency, details } = accounts;
  const account = details[0];
  const fiatBalance = Number(account?.fiat_balance || 0);

  const { currencies } = global;
  const displayOptions = currencies.config.display_options;

  const [withdrawFee, setWithdrawFee] = useState<AccountsEstimateFeeResponse>();
  const { currency = iban_currency, subtotal, fee, total } = withdrawFee || {};

  const isError = amount !== '' && Number(amount) > fiatBalance;

  useEffect(() => {
    if (Number(amount) > 0) {
      const data = { iban, currency, amount };

      debounced(() => {
        dispatch(accountsActions.getAccountEstimateFee(data))
          .unwrap()
          .then(res => setWithdrawFee(res.data))
          .catch(err => err);
      });
    } else setWithdrawFee(undefined);
  }, [amount, iban]);

  const handleChangeAmount = (value: string) =>
    setContext(draft => {
      draft.amount = value;
    });

  const isSubmitDisabled = !withdrawFee || accounts.loaders.getAccountEstimateFee;

  return (
    <Routes>
      <Route
        path="*"
        element={
          <AccountSendLayoutOld className={cn(null, [className])}>
            <LoaderLayout isLoading={accounts.loaders.getAccounts !== false}>
              <div className={cn('amount')} data-testid={ti('amount')}>
                <MoneyInputOld
                  label={t('informer.amount')}
                  value={amount}
                  onChange={handleChangeAmount}
                  error={isError}
                />
                <div className={cn('pseudo-select')}>{CONST.accountsCurrency}</div>
              </div>
              {isError && (
                <div className={cn('error')} data-testid={ti('error')}>
                  {t('iban.send_max_amount', { amount: fiatBalance })}
                </div>
              )}
              <LimitsOld
                className={cn('limits')}
                limits={{ [CONST.accountsCurrency]: { max: `${fiatBalance}` } }}
                currency={CONST.accountsCurrency}
                displayOptions={displayOptions}
                onClick={handleChangeAmount}
                isDisplayCurrency
                data-testid={ti('limits')}
              />
              <FeeInformer
                className={cn('fee-informer')}
                amountTotal={total}
                amountSubtotal={subtotal}
                fee={fee}
                currency={currency}
                feeCurrency={currency}
                operationType={CONST.operations.buy}
                currencies={currencies}
                bordered
                data-testid={ti('fee-informer')}
              />
              <Form isSubmitDisabled={isSubmitDisabled} />
            </LoaderLayout>
          </AccountSendLayoutOld>
        }
      />
      <Route path="verification" element={<SendVerification />} />
    </Routes>
  );
};

export default withCustomProvider(AccountsSend);
